import WasteCenterSchema from '~/app/common/schemas/WasteCenterSchema';
import EndpointFactory from '~~/app/factories/EndpointFactory';
import { ModelType } from '~~/app/base/schemas/BaseSchema';
import WasteCenterType from '@/app/worksite/enums/WasteCenterType';
import type WasteCenter from '@/app/common/models/WasteCenter';

/**
 * The "Ajouter un centre de tri" form, shared by its standalone modal
 * (`common/waste-center/FormModal.vue`) and by the panel the mobile waste settings push
 * (`Form/mobile/sheets/WasteCenterSheet.vue`), so the two cannot drift.
 *
 * A created centre is announced by the endpoint (`common:waste-center:created`), which is
 * what selects it in the waste-centre picker that asked for it.
 */
export default function useWasteCenterForm() {
    const form = useForm<{
        title: string | null;
        centerType: WasteCenterType | null;
        address: Address | null;
    }>({
        title: null,
        centerType: null,
        address: null,
    });

    /** Resolves to the created centre, or `null` when it was refused, so callers only leave on success. */
    const submit = async (): Promise<WasteCenter | null> => {
        const schema = WasteCenterSchema.make({ attributes: form.fields });
        const endpoint = EndpointFactory.make(ModelType.WASTE_CENTER);

        const response = await form.loadUntil(endpoint.storeOrUpdate(schema));
        // `loadUntil` already puts validation errors under their field and announces them
        // with its own toast; a generic "an error occurred" on top only doubled it.
        if (response.error || !response.data) {
            if (!response.validationErrors) useToasteoError();
            return null;
        }

        useToasteoSuccess();
        form.reset();
        return response.data as WasteCenter;
    };

    return { form, submit };
}
